import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { InformationEntityComponent } from '../../../../shared/components/information-entity/information-entity.component';
import { MultiSelectChangeEvent, MultiSelectModule } from 'primeng/multiselect';
import { TemplateDirective } from '../../../../shared/directives/template.directive';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { REPROCESS_API_ACTIONS } from '../../../../store/actions/reprocess.actions';
import { REPROCESS_REDUCER } from '../../../../store/reducers/reprocess.reducer';
import { AsyncPipe } from '@angular/common';
import { filter, firstValueFrom, Observable } from 'rxjs';
import { ScreenLoaderComponent } from '../../../../shared/components/loader/screen-loader.component';

@Component({
  selector: 'cv-reprocessing-tools',
  standalone: true,
  templateUrl: './reprocessing-tools.component.html',
  styleUrl: './reprocessing-tools.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AccordionModule,
    InformationEntityComponent,
    MultiSelectModule,
    TemplateDirective,
    ButtonModule,
    FormsModule,
    AsyncPipe,
    ScreenLoaderComponent,
  ],
})
export class ReprocessingToolsComponent {
  readonly dropdownOptions: IReprocessDropdownOption[] = [
    { label: 'Conversation Insights', value: EReprocessService.conversationIsnights },
    { label: 'Performance Insights', value: EReprocessService.performanceInsights },
    { label: 'Transcript', value: EReprocessService.transcript },
  ];
  dropdownValue: EReprocessService[] = [];
  reprocessLoading$: Observable<boolean> = this.store.select(REPROCESS_REDUCER.isLoading);

  constructor(
    private readonly toastService: MessageService,
    private readonly store: Store,
  ) {}

  async reprocess(): Promise<void> {
    this.store.dispatch(REPROCESS_API_ACTIONS.reprocess({ data: this.dropdownValue }));

    await firstValueFrom(this.reprocessLoading$.pipe(filter(Boolean)));
    this.toastService.add({
      severity: 'success',
      summary: 'Success',
      detail:
        'Reprocess initiated. This operation may take a few minutes to complete. Please wait, then refresh the page.',
    });
    this.dropdownValue = [];
  }

  reprocessServiceValueChanged({ itemValue: { value: selectedOptionValue }, value }: MultiSelectChangeEvent): void {
    const isTranscriptOptionSelected = (value as EReprocessService[]).includes(EReprocessService.transcript);
    const isTranscriptOptionClicked = selectedOptionValue === EReprocessService.transcript;

    if (!isTranscriptOptionClicked && isTranscriptOptionSelected) {
      this.dropdownValue = this.dropdownValue.filter(
        (currentValue) => currentValue !== EReprocessService.transcript && currentValue !== selectedOptionValue,
      );
    } else if (isTranscriptOptionSelected && isTranscriptOptionClicked) {
      this.dropdownValue = Object.values(EReprocessService).map((key) => key);
    }
  }
}

export enum EReprocessService {
  conversationIsnights = 'ConversationInsights',
  performanceInsights = 'PerformanceInsights',
  transcript = 'Transcript',
}

interface IReprocessDropdownOption {
  label: string;
  value: EReprocessService;
}
